<template>
  <div v-if="recurrence || nextOccurrenceAt" class="flex flex-col gap-1.5">
    
    <div v-if="shouldShowNextOccurrence" class="flex flex-row items-center">
      <BaseIcon size="18" class="mr-1">today</BaseIcon>
      <span class="text-body-medium">
        {{ $t('survey.schedule.nextOccurrence') }}:
        <span class="capitalize">
          {{
            useDisplayDate(effectiveNextOccurrenceAt, {
              dayName: 'short',
            })
          }}
        </span>
      </span>
    </div>
    <div v-if="recurrence && showRecurrence" class="flex flex-row items-center">
      <BaseIcon size="18" class="mr-1">repeat</BaseIcon>
      <span class="text-body-medium">
        {{ $t('survey.schedule.recurrence') }}:
        <span class="capitalize">
          {{ recurrenceSummaryText }}
        </span>
      </span>
    </div>
  </div>
  <span v-else-if="numberOfSurveys === 1" class="text-body-medium"
    >{{ $t('survey.schedule.oneShot') }}: {{ useDisplayRelativeDate(createdAt) }}</span
  >
  <span v-else class="text-body-medium">{{ $t('survey.schedule.paused') }}</span>
</template>
<script setup lang="ts">
  import BaseIcon from '@/core/components/base/icon/BaseIcon.vue'
  import { useDisplayDate } from '@/core/composables/use-display-date'
  import { useDisplayRelativeDate } from '@/core/composables/use-display-future-date'
  import type { CoachSurvey } from '../composables/use-coach-survey'
  import { useSurveyRecurrenceSummary } from '@/modules/start-survey/composables/use-survey-recurrence-summary'
  import { computed } from 'vue'

  interface Props {
    recurrence?: CoachSurvey['recurrence']
    nextOccurrenceAt?: string
    createdAt: string
    numberOfSurveys: number
    showNextOccurence?: boolean
    showRecurrence?: boolean
    showCreatedAt?: boolean
  }

  const {
    recurrence,
    nextOccurrenceAt,
    showNextOccurence = true,
    showRecurrence = true,
  } = defineProps<Props>()

  const effectiveNextOccurrenceAt = computed(
    () => nextOccurrenceAt ?? recurrence?.nextOccurrenceAt ?? '',
  )

  const shouldShowNextOccurrence = computed(() => {
    if (!effectiveNextOccurrenceAt.value) {
      return false
    }
    if (showNextOccurence) {
      return true
    }
    return !recurrence
  })

  const { recurrenceSummaryText } = useSurveyRecurrenceSummary(
    computed(() => new Date(effectiveNextOccurrenceAt.value || new Date())),
    computed(() => recurrence?.interval),
    computed(() => recurrence?.frequency),
    true,
  )
</script>
